import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Copy, Folder, Loader2 } from "lucide-react";
import { buildLocalProjectPath } from "@shared/local-project-path";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/contexts/auth-context";
import { useLocalDropboxRoot } from "@/hooks/use-local-dropbox-root";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { AccountSettingsDialog } from "@/components/account-settings-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

type Props = { projectId: string; projectName: string; isReadOnly?: boolean };

export function ProjectIdCopyMenu(props: Props) {
  const { user } = useAuth();
  return <ProjectIdCopyMenuContent key={`${user?.id ?? "signed-out"}:${props.projectId}`} {...props} userId={user?.id ?? null} />;
}

function ProjectIdCopyMenuContent({ projectId, projectName, isReadOnly, userId }: Props & { userId: number | null }) {
  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [manualCopy, setManualCopy] = useState<{ text: string; label: string } | null>(null);
  const { toast } = useToast();
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);
  const preferences = useLocalDropboxRoot(userId, open);
  const root = preferences.data?.localDropboxRootPath ?? null;
  const folder = useQuery({
    queryKey: ["/api/preview-shares/local-project-folder", { projectId, userId, root }],
    enabled: open && userId !== null && !!root && preferences.isSuccess && !preferences.isFetching,
    queryFn: async ({ signal }) => {
      const response = await apiRequest("GET", `/api/preview-shares/local-project-folder/${encodeURIComponent(projectId)}`);
      const body = await response.json() as { relativePath?: unknown };
      signal.throwIfAborted();
      if (typeof body.relativePath !== "string" || !body.relativePath) {
        throw new Error("Dropbox did not return a specific project folder.");
      }
      return buildLocalProjectPath(root!, body.relativePath);
    },
    // Keep verified results for this session, scoped to account + project + root.
    // Dropbox renames can be picked up explicitly with Refresh local path.
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const copy = async (text: string, label: string) => {
    setOpen(false);
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard access is unavailable.");
      // This call starts directly inside the selection event, after folder resolution.
      await navigator.clipboard.writeText(text);
      if (active.current) toast({ title: "Copied to clipboard", description: `${label}: ${text}` });
    } catch {
      if (active.current) setManualCopy({ text, label });
    }
  };
  const menuClass = "text-gray-200 focus:bg-gray-700 focus:text-white cursor-pointer";
  const preferencesLoading = preferences.isPending || preferences.isFetching;
  const folderLoading = !!root && (folder.isPending || folder.isFetching);
  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={`Copy actions for ${projectId}`}
            className={cn("text-sm font-medium cursor-pointer hover:text-blue-400 transition-colors select-none text-left", isReadOnly && "text-gray-400 hover:text-gray-400")}
            title="Click to copy"
            onClick={event => event.stopPropagation()}
          >{projectId}</button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="navy-card border-gray-600 z-50 max-w-[min(360px,calc(100vw-2rem))]"
          onClick={event => event.stopPropagation()}
          onCloseAutoFocus={event => { if (settingsOpen || manualCopy) event.preventDefault(); }}
        >
          <DropdownMenuItem className={menuClass} onSelect={() => void copy(projectId, "Project ID")}>
            <Copy className="mr-2 h-4 w-4" /><span>Copy ID: {projectId}</span>
          </DropdownMenuItem>
          <DropdownMenuItem className={menuClass} onSelect={() => void copy(`${projectId}_${projectName.trim().replace(/\s+/g, "_")}`, "Folder Name")}>
            <Copy className="mr-2 h-4 w-4" /><span>Copy Folder Name</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            className={menuClass}
            disabled={userId === null || preferencesLoading || preferences.isError || (!!root && (folderLoading || folder.isError || !folder.data))}
            onSelect={() => {
              if (!root) {
                setOpen(false);
                setSettingsOpen(true);
              } else if (folder.data) {
                void copy(folder.data, "Local Project Path");
              }
            }}
          >
            {preferencesLoading || folderLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Folder className="mr-2 h-4 w-4" />}
            <span>Copy Local Path</span>
          </DropdownMenuItem>
          <div className="px-2 pb-2 pt-1 text-xs text-gray-400 break-words">
            {userId === null ? <p>Sign in to use your saved local path.</p>
              : preferencesLoading ? <p role="status">Loading your saved path…</p>
              : preferences.isError ? <p role="alert">{preferences.error instanceof Error ? preferences.error.message : "Unable to load your saved path."}</p>
              : !root ? <p>Select Copy Local Path to set your local Projects folder in Account Settings.</p>
              : folderLoading ? <p role="status">Finding the exact Dropbox project folder…</p>
              : folder.isError ? <p role="alert">{folder.error instanceof Error ? folder.error.message : "Unable to find this project folder."}</p>
              : <p>Paste into Finder&apos;s Go to Folder (Shift–Command–G) or File Explorer&apos;s address bar. The folder must be included in local Dropbox sync.</p>}
          </div>
          {(preferences.isError || folder.isError) && (
            <DropdownMenuItem
              className={menuClass}
              onSelect={event => {
                event.preventDefault();
                if (preferences.isError) void preferences.refetch();
                else void folder.refetch();
              }}
            >Retry loading local path</DropdownMenuItem>
          )}
          {root && folder.isSuccess && !preferencesLoading && !folderLoading && (
            <DropdownMenuItem
              className={menuClass}
              onSelect={event => {
                event.preventDefault();
                void folder.refetch();
              }}
            >Refresh local path</DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {settingsOpen && <AccountSettingsDialog open onOpenChange={setSettingsOpen} />}
      <Dialog open={manualCopy !== null} onOpenChange={value => { if (!value) setManualCopy(null); }}>
        <DialogContent className="bg-gray-800 border-gray-600 text-gray-200">
          <DialogHeader>
            <DialogTitle>Copy {manualCopy?.label}</DialogTitle>
            <DialogDescription className="text-gray-400">
              Your browser did not allow clipboard access. Select the text below and copy it manually.
            </DialogDescription>
          </DialogHeader>
          <Input
            aria-label="Text to copy manually"
            readOnly
            value={manualCopy?.text ?? ""}
            onFocus={event => event.currentTarget.select()}
            onClick={event => event.currentTarget.select()}
            className="font-mono bg-gray-900 border-gray-600"
          />
          <Button type="button" onClick={() => setManualCopy(null)}>Done</Button>
        </DialogContent>
      </Dialog>
    </>
  );
}